import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import {
  useLoanSimulation,
  useAmortizationSchedule,
  useApplicablePolicy,
} from '../../../hooks/useCredit';
import type { CreditProductType, AmortizationType, SimulationResult } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR');
};

const productTypeOptions = [
  { value: 'PERSONAL_LOAN', label: 'Empréstimo Pessoal' },
  { value: 'PAYROLL_LOAN', label: 'Crédito Consignado' },
  { value: 'WORKING_CAPITAL', label: 'Capital de Giro' },
];

const amortizationOptions = [
  { value: 'PRICE', label: 'Price (Parcelas Fixas)' },
  { value: 'SAC', label: 'SAC (Parcelas Decrescentes)' },
];

export function LoanSimulator() {
  const [productType, setProductType] = useState<CreditProductType>('PERSONAL_LOAN');
  const [amount, setAmount] = useState(500000); // R$ 5.000
  const [installments, setInstallments] = useState(12);
  const [amortizationType, setAmortizationType] = useState<AmortizationType>('PRICE');
  const [showSchedule, setShowSchedule] = useState(false);

  const { data: policy, isLoading: policyLoading } = useApplicablePolicy(productType);
  const simulation = useLoanSimulation();
  const schedule = useAmortizationSchedule();

  const [result, setResult] = useState<SimulationResult | null>(null);

  useEffect(() => {
    if (policy) {
      // Ajustar valores para os limites da política
      if (amount < policy.minAmount) setAmount(policy.minAmount);
      if (amount > policy.maxAmount) setAmount(policy.maxAmount);
      if (installments < policy.minInstallments) setInstallments(policy.minInstallments);
      if (installments > policy.maxInstallments) setInstallments(policy.maxInstallments);
    }
  }, [policy]);

  const handleSimulate = async () => {
    try {
      const result = await simulation.mutateAsync({
        productType,
        amount,
        installments,
        amortizationType,
      });
      setResult(result);
    } catch (error) {
      console.error('Erro na simulação:', error);
    }
  };

  const handleShowSchedule = async () => {
    try {
      await schedule.mutateAsync({
        productType,
        amount,
        installments,
        amortizationType,
      });
      setShowSchedule(true);
    } catch (error) {
      console.error('Erro ao gerar tabela:', error);
    }
  };

  if (policyLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-96 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Simulador de Empréstimo</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulário de Simulação */}
        <Card>
          <CardHeader>
            <CardTitle>Configure seu Empréstimo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              label="Tipo de Empréstimo"
              value={productType}
              onChange={(e) => setProductType(e.target.value as CreditProductType)}
              options={productTypeOptions}
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                Valor: {formatCurrency(amount)}
              </label>
              <input
                type="range"
                min={policy?.minAmount || 50000}
                max={policy?.maxAmount || 10000000}
                step={10000}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(policy?.minAmount || 50000)}</span>
                <span>{formatCurrency(policy?.maxAmount || 10000000)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Parcelas: {installments}x
              </label>
              <input
                type="range"
                min={policy?.minInstallments || 3}
                max={policy?.maxInstallments || 48}
                value={installments}
                onChange={(e) => setInstallments(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{policy?.minInstallments || 3}x</span>
                <span>{policy?.maxInstallments || 48}x</span>
              </div>
            </div>

            <Select
              label="Tipo de Amortização"
              value={amortizationType}
              onChange={(e) => setAmortizationType(e.target.value as AmortizationType)}
              options={amortizationOptions}
            />

            <Button
              onClick={handleSimulate}
              disabled={simulation.isPending}
              className="w-full"
            >
              {simulation.isPending ? 'Simulando...' : 'Simular'}
            </Button>
          </CardContent>
        </Card>

        {/* Resultado da Simulação */}
        <Card>
          <CardHeader>
            <CardTitle>Resultado da Simulação</CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <p className="text-gray-500 text-center py-8">
                Configure os valores e clique em "Simular" para ver o resultado.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-600">Parcela Mensal</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatCurrency(result.installmentAmount)}
                  </p>
                  {result.lastInstallmentAmount && (
                    <p className="text-sm text-gray-500">
                      Última parcela: {formatCurrency(result.lastInstallmentAmount)}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Valor Solicitado</p>
                    <p className="font-medium">{formatCurrency(result.amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total a Pagar</p>
                    <p className="font-medium">{formatCurrency(result.totalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total de Juros</p>
                    <p className="font-medium">{formatCurrency(result.totalInterest)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">IOF</p>
                    <p className="font-medium">{formatCurrency(result.totalIOF)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Taxa Mensal</p>
                    <p className="font-medium">
                      {(result.monthlyInterestRate * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">CET (a.a.)</p>
                    <p className="font-medium text-lg">{result.cet.toFixed(2)}%</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-sm">
                    <span>Primeiro Vencimento:</span>
                    <span>{formatDate(result.firstDueDate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Último Vencimento:</span>
                    <span>{formatDate(result.lastDueDate)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleShowSchedule}
                    disabled={schedule.isPending}
                    className="flex-1"
                  >
                    Ver Tabela
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/banking/credit/request'}
                    className="flex-1"
                  >
                    Solicitar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Amortização */}
      {showSchedule && schedule.data && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Tabela de Amortização</CardTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSchedule(false)}
              >
                Fechar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Parcela</th>
                    <th className="text-left py-2">Vencimento</th>
                    <th className="text-right py-2">Principal</th>
                    <th className="text-right py-2">Juros</th>
                    <th className="text-right py-2">IOF</th>
                    <th className="text-right py-2">Total</th>
                    <th className="text-right py-2">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.data.installments.map((inst) => (
                    <tr key={inst.number} className="border-b">
                      <td className="py-2">{inst.number}</td>
                      <td className="py-2">{formatDate(inst.dueDate)}</td>
                      <td className="text-right py-2">{formatCurrency(inst.principal)}</td>
                      <td className="text-right py-2">{formatCurrency(inst.interest)}</td>
                      <td className="text-right py-2">{formatCurrency(inst.iof)}</td>
                      <td className="text-right py-2 font-medium">
                        {formatCurrency(inst.total)}
                      </td>
                      <td className="text-right py-2">{formatCurrency(inst.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-medium">
                    <td colSpan={2} className="py-2">Total</td>
                    <td className="text-right py-2">
                      {formatCurrency(schedule.data.summary.totalPrincipal)}
                    </td>
                    <td className="text-right py-2">
                      {formatCurrency(schedule.data.summary.totalInterest)}
                    </td>
                    <td className="text-right py-2">
                      {formatCurrency(schedule.data.summary.totalIOF)}
                    </td>
                    <td className="text-right py-2">
                      {formatCurrency(schedule.data.summary.totalAmount)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default LoanSimulator;
